import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp,
  getDocs,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore'
import { db } from 'config/firebase'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { PickupLocation } from '@otw/models'

interface UsePickupLocationsReturn {
  pickupLocations: PickupLocation[] | undefined
  isLoading: boolean
  error: Error | null
  createPickupLocation: (location: Omit<PickupLocation, 'id' | 'created_at' | 'updated_at'>) => Promise<PickupLocation>
  updatePickupLocation: (location: PickupLocation) => Promise<PickupLocation>
  deletePickupLocation: (id: string) => Promise<string>
}

const stripUndefinedFields = <T extends Record<string, any>>(obj: T): Partial<T> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  ) as Partial<T>
}

export const usePickupLocations = (): UsePickupLocationsReturn => {
  const { showSuccess, showError } = useSnackbar()
  const queryClient = useQueryClient()
  const queryKey = ['pickupLocations']

  const {
    data: pickupLocations,
    isLoading,
    error,
  } = useQuery<PickupLocation[]>({
    queryKey,
    queryFn: async () => {
      try {
        const collectionRef = collection(db, 'pickup_locations')
        const snapshot = await getDocs(collectionRef)
        return snapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({
          id: doc.id,
          ...doc.data(),
        })) as PickupLocation[]
      } catch (error) {
        throw error
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const createPickupLocationMutation = useMutation({
    mutationFn: async (location: Omit<PickupLocation, 'id' | 'created_at' | 'updated_at'>) => {
      try {
        const sanitizedLocation = stripUndefinedFields(location)
        const docRef = await addDoc(collection(db, 'pickup_locations'), {
          ...sanitizedLocation,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        })
        return { id: docRef.id, ...sanitizedLocation } as PickupLocation
      } catch (error) {
        throw error
      }
    },
    onMutate: async newLocation => {
      await queryClient.cancelQueries({ queryKey })
      const previousLocations = queryClient.getQueryData<PickupLocation[]>(queryKey)

      const tempId = `temp-${Date.now()}`
      const optimisticLocation = {
        ...newLocation,
        id: tempId,
        created_at: new Date(),
        updated_at: new Date(),
      } as PickupLocation

      queryClient.setQueryData<PickupLocation[]>(queryKey, old => [...(old || []), optimisticLocation])

      return { previousLocations, optimisticLocation, tempId }
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousLocations)
      showError(`Failed to create pickup location: ${err instanceof Error ? err.message : 'Unknown error'}`)
    },
    onSuccess: (result, variables, context) => {
      // Update the cache to replace the temporary ID with the real one
      if (context?.tempId) {
        queryClient.setQueryData<PickupLocation[]>(queryKey, old => {
          if (!old) return [result]
          return old.map(location => (location.id === context.tempId ? result : location))
        })
      }
      showSuccess('Pickup location created successfully')
    },
  })

  const updatePickupLocationMutation = useMutation({
    mutationFn: async (location: PickupLocation) => {
      if (!location.id) throw new Error('Location ID is required')
      try {
        const { id, ...locationWithoutId } = location
        const sanitizedLocation = stripUndefinedFields(locationWithoutId)
        await updateDoc(doc(db, 'pickup_locations', location.id), {
          ...sanitizedLocation,
          updated_at: serverTimestamp(),
        })
        return { ...location, ...sanitizedLocation }
      } catch (error) {
        throw error
      }
    },
    onMutate: async updatedLocation => {
      await queryClient.cancelQueries({ queryKey })
      const previousLocations = queryClient.getQueryData<PickupLocation[]>(queryKey)

      queryClient.setQueryData<PickupLocation[]>(queryKey, old => {
        if (!old) return old
        return old.map(location => 
          location.id === updatedLocation.id 
            ? { ...location, ...updatedLocation, updated_at: new Date() }
            : location
        )
      })

      return { previousLocations }
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousLocations)
      showError(`Failed to update pickup location: ${err instanceof Error ? err.message : 'Unknown error'}`)
    },
    onSuccess: () => {
      showSuccess('Pickup location updated successfully')
    },
  })

  const deletePickupLocationMutation = useMutation({
    mutationFn: async (id: string) => {
      try {
        await deleteDoc(doc(db, 'pickup_locations', id))
        return id
      } catch (error) {
        throw error
      }
    },
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey })
      const previousLocations = queryClient.getQueryData<PickupLocation[]>(queryKey)

      queryClient.setQueryData<PickupLocation[]>(queryKey, old => {
        if (!old) return old
        return old.filter(location => location.id !== id)
      })

      return { previousLocations }
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousLocations)
      showError(`Failed to delete pickup location: ${err instanceof Error ? err.message : 'Unknown error'}`)
    },
    onSuccess: () => {
      showSuccess('Pickup location deleted successfully')
    },
  })

  return {
    pickupLocations,
    isLoading,
    error: error as Error | null,
    createPickupLocation: createPickupLocationMutation.mutateAsync,
    updatePickupLocation: updatePickupLocationMutation.mutateAsync,
    deletePickupLocation: deletePickupLocationMutation.mutateAsync,
  }
} 